import React, { useState, useEffect, useRef, useCallback } from "react";
import { Mic, Upload, Square, Loader2, Trash2, Users, X, FileAudio, Folder, RefreshCw, Gauge, Radio } from "lucide-react";
import { createMeeting, listMeetings, getMeeting, deleteMeeting } from "../lib/api";
import { toast } from "sonner";

const CAT_COLORS = {
  "Real Estate": "#DBEAFE", "Business": "#DCFCE7", "Personal": "#FEF3C7",
  "Litigation": "#FEE2E2", "Trust/Estate": "#EDE9FE", "Other": "#F1F5F9",
};
const CONF_COLOR = { high: "#15803D", medium: "#D97706", low: "#DC2626" };

function MeetingDetail({ id, requesterId, onClose, onDeleted }) {
  const [m, setM] = useState(null);
  useEffect(() => { getMeeting(id, requesterId).then(setM).catch(() => {}); }, [id]); // eslint-disable-line
  if (!m) return null;
  const a = m.analysis || {};
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="meeting-modal">
      <div className="bg-[#FAFAF7] w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border-3 border-[#1A1C19] yb-fade-up">
        <div className="sticky top-0 bg-[#FFD600] border-b-3 border-[#1A1C19] p-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h2 className="yb-heading text-xl font-extrabold truncate">{m.title}</h2>
            <p className="text-sm text-[#1A1C19]/70">{m.category} · {a.num_speakers ?? "?"} speakers</p>
          </div>
          <button onClick={onClose} data-testid="meeting-close" className="yb-tactile bg-white p-3 ml-2"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <div className="bg-white rounded-2xl border-3 border-[#1A1C19] p-4">
            <p className="font-extrabold mb-1">Crux</p>
            <p className="text-lg">{a.crux || a.summary}</p>
          </div>
          <div className="bg-white rounded-2xl border-2 border-[#1A1C19] p-4">
            <p className="font-extrabold mb-1">Summary</p>
            <p>{a.summary}</p>
          </div>
          {(a.speakers || []).length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-[#1A1C19] p-4">
              <p className="font-extrabold mb-2 flex items-center gap-2"><Users className="w-5 h-5" /> Speakers</p>
              {(a.speakers).map((s, i) => (
                <div key={i} className="flex justify-between border-b border-[#eee] py-1 last:border-0">
                  <span>{s.name || s.label}</span><span className="text-[#50564F] text-sm">{s.talk_share}</span>
                </div>
              ))}
            </div>
          )}
          {(a.per_speaker || []).length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-[#1A1C19] p-4 space-y-2">
              <p className="font-extrabold flex items-center gap-2"><Gauge className="w-5 h-5" /> Tone & confidence (AI estimate)</p>
              {(a.per_speaker).map((s, i) => (
                <div key={i} className="text-sm">
                  <span className="font-bold">{s.name_or_label}:</span> {s.tone}
                  <span className="ml-2 px-2 py-0.5 rounded-full text-white text-xs" style={{ background: CONF_COLOR[s.confidence_estimate] || "#50564F" }}>{s.confidence_estimate}</span>
                  {s.note && <span className="text-[#50564F]"> — {s.note}</span>}
                </div>
              ))}
            </div>
          )}
          {a.honesty_estimate && (a.honesty_estimate.level || a.honesty_estimate.note) && (
            <div className="bg-[#FEF3C7] rounded-2xl border-2 border-[#D97706] p-4">
              <p className="font-extrabold">Honesty / consistency (AI estimate)</p>
              <p>{a.honesty_estimate.level} — {a.honesty_estimate.note}</p>
            </div>
          )}
          {(a.action_items || []).length > 0 && (
            <div className="bg-[#DCFCE7] rounded-2xl border-2 border-[#15803D] p-4">
              <p className="font-extrabold mb-1">Action items</p>
              <ul className="list-disc pl-5">{a.action_items.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          )}
          {(a.decisions || []).length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-[#1A1C19] p-4">
              <p className="font-extrabold mb-1">Decisions</p>
              <ul className="list-disc pl-5">{a.decisions.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          )}
          <details className="bg-white rounded-2xl border-2 border-[#1A1C19] p-4">
            <summary className="font-bold cursor-pointer">Full transcript</summary>
            <p className="mt-2 whitespace-pre-wrap text-[#50564F] text-sm">{m.transcript}</p>
          </details>
          <p className="text-xs text-[#50564F] italic border-l-4 border-[#1A1C19] pl-3">{a.disclaimer}</p>
          <button onClick={async () => { await deleteMeeting(m.id, requesterId); toast.success("Deleted"); onDeleted(); }} data-testid="meeting-delete" className="yb-tactile w-full py-3 bg-[#DC2626] text-white font-bold flex items-center justify-center gap-2">
            <Trash2 className="w-5 h-5" /> Delete meeting
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Meetings({ session }) {
  const [meetings, setMeetings] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [active, setActive] = useState(null);
  const [wake, setWake] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const uploadRef = useRef(null);
  const wakeRecogRef = useRef(null);
  const actionRef = useRef({});

  const refresh = useCallback(async () => {
    try { setMeetings(await listMeetings(session.user_id)); } catch (e) { /* ignore */ }
  }, [session.user_id]);
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const process = async (blob, filename) => {
    setBusy(true);
    toast.info("Transcribing & analysing… this can take a minute");
    try {
      const m = await createMeeting(session.user_id, title, blob, filename);
      setTitle("");
      await refresh();
      setActive(m.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not process audio");
    } finally {
      setBusy(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        process(new Blob(chunksRef.current, { type: "audio/webm" }), "recording.webm");
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error("Microphone didn't start"); }
  };
  const stopRec = () => { if (recRef.current && recording) { recRef.current.stop(); setRecording(false); } };

  // keep latest handlers/state for the wake-word listener (avoids stale closures)
  actionRef.current = { startRec, stopRec, recording, busy };

  // Hands-free wake word: "let's go nexus" starts, "nexus stop" ends & analyses
  useEffect(() => {
    if (!wake) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (!SR || isIOS) {
      toast.message("Hands-free voice commands need Chrome or Android. On iPhone, tap Record instead.", { duration: 6000 });
      setWake(false);
      return;
    }
    const recog = new SR();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = "en-US";
    let lastFire = 0;
    recog.onresult = (ev) => {
      const now = Date.now();
      if (now - lastFire < 1500) return;
      const text = Array.from(ev.results).map((r) => r[0].transcript).join(" ").toLowerCase();
      const a = actionRef.current;
      const wantsStart = /(let'?s go nexus|start nexus|nexus start|nexus record)/.test(text);
      const wantsStop = /(nexus stop|stop nexus|stop recording|nexus done)/.test(text);
      if (wantsStop && a.recording) { lastFire = now; a.stopRec(); toast.info("Nexus stopping & analysing…"); }
      else if (wantsStart && !a.recording && !a.busy) { lastFire = now; a.startRec(); toast.success("Nexus recording"); }
    };
    recog.onend = () => { if (wake) { try { recog.start(); } catch (e) {} } };
    recog.onerror = (e) => {
      if (e && (e.error === "not-allowed" || e.error === "service-not-allowed")) {
        toast.error("Mic blocked — allow microphone access to use voice commands.");
        setWake(false);
      }
    };
    wakeRecogRef.current = recog;
    try { recog.start(); } catch (e) {}
    return () => { try { recog.stop(); } catch (e) {} };
  }, [wake]); // eslint-disable-line

  const grouped = meetings.reduce((acc, m) => {
    const c = m.category || "Other"; (acc[c] = acc[c] || []).push(m); return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="meetings-panel">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#1A1C19] text-[#FFD600] flex items-center justify-center border-3 border-[#1A1C19]"><FileAudio className="w-7 h-7" /></div>
        <div>
          <h2 className="yb-heading text-2xl font-extrabold">MeetURai</h2>
          <p className="text-sm text-[#50564F]">Record or upload meetings & phone calls — AI minutes, speakers, tone</p>
        </div>
      </div>

      <div className="bg-[#FEFCE8] rounded-2xl border-3 border-[#1A1C19] p-4 space-y-3" style={{ boxShadow: "0 4px 0 0 #1A1C19" }}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Meeting title (optional)" data-testid="meeting-title-input"
          className="w-full yb-tactile bg-white px-4 py-3 outline-none" />
        <button onClick={() => setWake((w) => !w)} data-testid="meeting-wake-toggle"
          className={`yb-tactile w-full py-3 font-bold flex items-center justify-center gap-2 ${wake ? "bg-[#15803D] text-white yb-glow-green" : "bg-white"}`}>
          <Radio className={`w-5 h-5 ${wake ? "animate-pulse" : ""}`} /> Hands-free {wake ? "ON — say “let's go nexus” / “nexus stop”" : "OFF"}
        </button>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={recording ? stopRec : startRec} disabled={busy} data-testid="meeting-record-button"
            className={`yb-tactile py-6 font-black text-lg flex items-center justify-center gap-2 ${recording ? "bg-[#DC2626] text-white yb-glow-red" : "bg-[#FFD600]"}`}>
            {recording ? <><Square className="w-6 h-6" /> Stop & analyse</> : <><Mic className="w-7 h-7" /> Record</>}
          </button>
          <button onClick={() => uploadRef.current?.click()} disabled={busy} data-testid="meeting-upload-button"
            className="yb-tactile py-6 bg-white font-black text-lg flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-7 h-7 animate-spin" /> : <Upload className="w-7 h-7" />} Upload audio
          </button>
          <input ref={uploadRef} type="file" accept="audio/*" className="hidden" data-testid="meeting-file-input"
            onChange={(e) => { if (e.target.files[0]) process(e.target.files[0], e.target.files[0].name); e.target.value = ""; }} />
        </div>
        <p className="text-xs text-[#50564F]">Phone calls: record with your phone's call-recorder app, then upload the file here.</p>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="yb-heading font-extrabold text-lg">Meeting folders ({meetings.length})</h3>
        <button onClick={refresh} className="yb-tactile bg-white p-2"><RefreshCw className="w-5 h-5" /></button>
      </div>
      {meetings.length === 0 && <p className="text-[#50564F]">No meetings yet.</p>}
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="space-y-2">
          <p className="font-bold text-[#50564F] flex items-center gap-2"><Folder className="w-5 h-5" /> {cat} ({items.length})</p>
          {items.map((m) => (
            <button key={m.id} onClick={() => setActive(m.id)} data-testid={`meeting-row-${m.id}`}
              className="yb-tactile w-full text-left p-4 flex items-center gap-3" style={{ background: CAT_COLORS[m.category] || "#fff" }}>
              <FileAudio className="w-6 h-6 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{m.title}</p>
                <p className="text-sm text-[#50564F] truncate">{m.folder_name || (m.analysis && m.analysis.crux) || ""}</p>
              </div>
            </button>
          ))}
        </div>
      ))}

      {active && <MeetingDetail id={active} requesterId={session.user_id} onClose={() => setActive(null)} onDeleted={() => { setActive(null); refresh(); }} />}
    </div>
  );
}
